/**
 * useIsAdmin — single source of truth for client-side admin gating.
 *
 * DB-backed (Task 7): delegates to useDbUser(), which reads the
 * authoritative `is_admin` column on the `users` table via `POST
 * /auth/sync`. Frontend `ADMIN_EMAILS` is no longer consulted — the
 * allowlist drifted from the DB flag admin impersonation now requires
 * (an admin has to be recognized server-side to hit the impersonation
 * endpoints), so every frontend admin gate reads the same source the
 * backend enforces.
 *
 * Returns `false` until the underlying `/auth/sync` call resolves (same
 * "default to the safer, hidden path" behavior the old email-allowlist
 * version had during initial auth load) — callers that need to distinguish
 * "still loading" from "confirmed non-admin" should use useDbUser()
 * directly instead (see AdminPage.tsx, which avoids redirecting until
 * `loading` is false).
 *
 * NOTE: This remains a UX-level gate for surfaces that aren't ready for
 * general users yet (UK/CA/AU regions while parser-quality work continues,
 * the Label Negotiations mode pending UX polish, the SBA Reviews source
 * pill pending product decision). The backend routes for these features
 * remain open to every allowlisted user — there is no defense-in-depth
 * server check, because the data isn't sensitive, just unpolished.
 * Bypassing this gate gets you broken UX, not unauthorized data.
 */
import { useDbUser } from './useDbUser';

export function useIsAdmin(): boolean {
  const { isAdmin } = useDbUser();
  return isAdmin;
}
